/**
 * Copyright ©2024 Drivepoint
 */

import React, {ReactNode, useEffect, useRef} from "react";
import {EventBus, useStateChange, useStore} from "@bainbridge-growth/node-frontend";
import DrivepointUser from "../../services/DrivepointUser.ts";
import PlanStore from "../../stores/PlanStore.ts";
import type {PlanProps} from "../../types/ExcelModel.ts";
import ErrorHandling from "../../utilities/ErrorHandling.ts";
import DPPageStatus, {DPPageStatusInterface} from "../DPPageStatus/DPPageStatus.tsx";
import "./Page.css";

export type PageProps = {
  children: ReactNode;
};

export default function Page(props: PageProps): any {

  const user = useStateChange<DrivepointUser>("user");

  const status = useRef<DPPageStatusInterface>();
  const [plan] = useStore<PlanProps, PlanStore>(PlanStore);

  useEffect(() => {
    const registrations = EventBus.registerMany("system:error", (event: any) => {
      if (event.type === "system:error") {
        handleErrorStatus(event.message, event.refresh);
      }
      if (event.type === "system:error:compatibility") {
        handleErrorStatus(event.message, event.refresh);
      }
    });
    return () => {
      EventBus.unregister(...registrations);
    };
  }, []);

  useEffect(() => {
    if (status) { status.current?.hide(); }
  }, [location.hash]);

  useEffect(() => {
    if (plan?.id === "not_smartmodel" && user) {
      logger.warn("[Page] Workbook is not a SmartModel \u2014 features that require a plan will be disabled. The agent remains available.", {
        reason: (plan as any).reason,
        diagnostics: (plan as any).diagnostics,
        error: (plan as any).error?.message
      });
    }
    status.current?.hide();
  }, [plan?.id, user]);

  function handleErrorStatus(message: string, refreshAction: () => void = () => window.location.reload()) {
    status.current?.show({
      variant: "error",
      title: "Something went wrong!",
      subtitle: `${message}\nPlease try again. If the issue persists please contact support.`,
      actions: [
        {icon: "refresh", title: "Refresh Add-in", click: refreshAction},
        {icon: "support_agent", title: "Contact Support", click: ErrorHandling.contactSupport, notHideAfter: true}
      ]
    });
  }

  return <div className="page">
    <DPPageStatus ref={status} />
    {props.children}
  </div>;

}
